/**
 * Cliente HTTP da API.
 *
 * Os tipos vêm de `@co/contracts` — a mesma fonte que o servidor valida. Se a
 * tela precisar de um campo que não está lá, o campo entra no contrato, não
 * numa interface paralela aqui.
 *
 * O token fica em `sessionStorage`: fechar a aba encerra a sessão e não sobra
 * credencial em máquina compartilhada.
 */
import type {
  Ator as AtorContrato,
  ConversaDoCard,
  FichaCard,
  RespostaCrm,
  CampanhaDetalhe,
  CampanhaResumo,
  Configuracoes,
  Kit,
  NumeroFicha,
  Plano,
  PlanoCampanha,
  PreviaImportacao,
  RespostaFila,
  RespostaHistorico,
  RespostaLeads,
  RespostaNumeros,
  RespostaPainel,
  UsuarioResumo,
} from "@co/contracts";

export type Ator = AtorContrato;
export type {
  ConversaDoCard,
  FichaCard,
  RespostaCrm,
  CampanhaDetalhe,
  CampanhaResumo,
  Configuracoes,
  Kit,
  NumeroFicha,
  Plano,
  PlanoCampanha,
  RespostaFila,
  RespostaHistorico,
  RespostaLeads,
  RespostaNumeros,
  RespostaPainel,
  UsuarioResumo,
};

const CHAVE_TOKEN = "autodis.token";

export class ErroApi extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

export const guardarToken = (t: string): void =>
  sessionStorage.setItem(CHAVE_TOKEN, t);
export const lerToken = (): string | null =>
  sessionStorage.getItem(CHAVE_TOKEN);
export const limparToken = (): void => sessionStorage.removeItem(CHAVE_TOKEN);

function query(p?: Record<string, string | number | boolean | undefined>): string {
  if (!p) return "";
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v !== undefined && v !== "") q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

async function chamar<T>(caminho: string, init?: RequestInit): Promise<T> {
  const token = lerToken();
  const r = await fetch(caminho, {
    ...init,
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  });

  if (r.status === 401) {
    limparToken();
    throw new ErroApi("Sessão expirada. Entre de novo.", 401);
  }

  const texto = await r.text();
  let corpo: unknown = null;
  try {
    corpo = texto ? JSON.parse(texto) : null;
  } catch {
    corpo = null;
  }

  if (!r.ok) {
    const c = corpo as { erro?: string; message?: string } | null;
    throw new ErroApi(
      c?.erro ?? c?.message ?? `Falha na requisição (${r.status}).`,
      r.status,
    );
  }
  return corpo as T;
}

// `exactOptionalPropertyTypes` está ligado: `body: undefined` não é o mesmo
// que ausência de `body`. Montar o objeto condicionalmente evita a asserção.
const post = (c: string, corpo?: unknown): Promise<unknown> =>
  chamar(
    c,
    corpo === undefined
      ? { method: "POST" }
      : { method: "POST", body: JSON.stringify(corpo) },
  );

export const api = {
  /* Sessão */
  entrar: (email: string, senha: string) =>
    chamar<{ token: string; usuario: { id: string; nome: string; perfil: string } }>(
      "/api/sessao",
      { method: "POST", body: JSON.stringify({ email, senha }) },
    ),
  quemSou: () => chamar<{ usuario: Ator }>("/api/sessao"),

  /* Painel */
  painel: () => chamar<RespostaPainel>("/api/painel"),

  /* Números */
  numeros: (f?: Record<string, string | number | undefined>) =>
    chamar<RespostaNumeros>(`/api/numeros${query(f)}`),
  numeroFicha: (id: string) => chamar<NumeroFicha>(`/api/numeros/${id}`),
  criarNumero: (dados: unknown) => post("/api/numeros", dados),
  editarNumero: (id: string, dados: unknown) =>
    chamar(`/api/numeros/${id}`, { method: "PATCH", body: JSON.stringify(dados) }),
  excluirNumero: (id: string) =>
    chamar(`/api/numeros/${id}`, { method: "DELETE" }),
  previaImportacao: () =>
    chamar<PreviaImportacao>("/api/numeros/importacao/previa"),
  importar: (dados: unknown) => post("/api/numeros/importacao", dados),
  // O servidor lê `observacao`, não `motivo`. Mandando o nome errado, o Zod
  // descartava a chave em silêncio e a justificativa de quem liberou o número
  // era jogada fora — justamente o texto que a liberação existe para guardar.
  liberar: (id: string, observacao?: string) =>
    post(`/api/numeros/${id}/liberacao`, { observacao }),
  revogar: (id: string, motivo: string) =>
    chamar(`/api/numeros/${id}/liberacao`, {
      method: "DELETE",
      body: JSON.stringify({ motivo }),
    }),

  /* Planos */
  planos: () => chamar<{ itens: Plano[] }>("/api/planos"),
  criarPlano: (dados: unknown) => post("/api/planos", dados),
  salvarPlano: (id: string, dados: unknown) =>
    chamar(`/api/planos/${id}`, { method: "PUT", body: JSON.stringify(dados) }),
  excluirPlano: (id: string) => chamar(`/api/planos/${id}`, { method: "DELETE" }),

  /* Fila */
  // Executa uma atividade agora, sem esperar o ciclo do motor. Não move a
  // hora agendada — ela é chave de idempotência.
  executarAtividade: (id: string) =>
    post(`/api/fila/${id}/executar`) as Promise<{ resultado: string; motivo: string }>,
  fila: (f?: Record<string, string | number | undefined>) =>
    chamar<RespostaFila>(`/api/fila${query(f)}`),
  cancelarAtividade: (id: string) =>
    chamar(`/api/fila/${id}`, { method: "DELETE" }),

  /* Histórico */
  historico: (f?: Record<string, string | number | undefined>) =>
    chamar<RespostaHistorico>(`/api/historico${query(f)}`),

  /* Campanhas */
  campanhas: () => chamar<{ itens: CampanhaResumo[] }>("/api/campanhas"),
  campanha: (id: string) => chamar<CampanhaDetalhe>(`/api/campanhas/${id}`),
  criarCampanha: (dados: unknown) => post("/api/campanhas", dados),
  planejar: (id: string, dados: unknown) =>
    chamar<PlanoCampanha>(`/api/campanhas/${id}/planejar`, {
      method: "POST",
      body: JSON.stringify(dados),
    }),
  // `confirmo` é exigido pela rota: aprovar é o momento em que a campanha
  // passa a consumir chip e base reais. Sem ele a chamada devolvia 400 e
  // NENHUMA campanha jamais foi aprovada por esta tela.
  aprovarCampanha: (id: string) =>
    post(`/api/campanhas/${id}/aprovar`, { confirmo: true }),
  // O motivo e obrigatorio no servidor. Chamar sem ele devolvia 400 e a tela
  // so dizia "a acao nao foi aceita" — pausar nunca funcionou.
  pausarCampanha: (id: string, motivo: string) =>
    post(`/api/campanhas/${id}/pausar`, { motivo }),
  abortarCampanha: (id: string, motivo: string) =>
    post(`/api/campanhas/${id}/abortar`, { motivo }),
  editarCampanha: (id: string, dados: unknown) =>
    chamar<{ id: string; motivo: string }>(`/api/campanhas/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dados),
    }),
  excluirCampanha: (id: string) =>
    chamar(`/api/campanhas/${id}`, { method: 'DELETE' }),
  retomarCampanha: (id: string) => post(`/api/campanhas/${id}/retomar`),
  importarBase: (id: string, dados: unknown) =>
    post(`/api/campanhas/${id}/base`, dados),

  /* Kits e produtos */
  kits: () => chamar<{ itens: Kit[] }>("/api/kits"),
  criarKit: (dados: unknown) => post("/api/kits", dados),
  produtos: () =>
    chamar<{
      itens: Array<{
        id: string;
        nome: string;
        familia: string | null;
        ativo: boolean;
        criadoEm: string;
        campanhasUsando: number;
      }>;
    }>("/api/produtos"),
  criarProduto: (dados: unknown) =>
    post("/api/produtos", dados) as Promise<{ id: string; motivo: string }>,
  // Não existe excluir produto — ver a nota em rotas/produtos.ts. Desativar
  // é `editarProduto(id, { ativo: false })`.
  editarProduto: (id: string, dados: unknown) =>
    chamar<{ id: string; motivo: string }>(`/api/produtos/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  /* Conversas — por CONTATO, juntando todos os números que falaram com ele */
  conversas: (f?: Record<string, string | number | undefined>) =>
    chamar<{
      itens: Array<{
        contatoId: string; nome: string | null; telefone: string;
        mensagens: number; recebidas: number; enviadas: number;
        ultimaEm: string; ultimaRecebidaEm: string | null;
        numerosEnvolvidos: number; aguardandoResposta: boolean;
        ultimoTexto: string | null; ultimaDirecao: string | null;
      }>;
    }>(`/api/conversas${query(f)}`),
  conversa: (id: string) =>
    chamar<{
      contato: { id: string; nome: string | null; telefone: string };
      mensagens: Array<{
        id: string; direcao: string; origem: string; tipo: string;
        texto: string | null; status: string | null; ocorridoEm: string;
        numeroExibicao: string | null; campanhaNome: string | null;
      }>;
    }>(`/api/conversas/${id}`),

  /* Repertório — as frases que o aquecimento manda */
  repertorio: () =>
    chamar<{
      itens: Array<{
        id: string; tipo: string; papel: string; texto: string; tamanho: string;
        tema: string; registro: string; risco: string; peso: number; ativo: boolean;
        usos: number; ultimoUso: string | null;
      }>;
      porTipo: Array<{ tipo: string; total: number; ativas: number }>;
    }>("/api/repertorio"),
  criarFrase: (dados: unknown) =>
    post("/api/repertorio", dados) as Promise<{ id: string; motivo: string }>,
  editarFrase: (id: string, dados: unknown) =>
    chamar<{ id: string; motivo: string }>(`/api/repertorio/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  /* Lotes — a procedência declarada da base */
  lotes: () =>
    chamar<{
      itens: Array<{
        id: string;
        rotulo: string;
        origem: string;
        naturezaTitulares: string;
        estado: string;
        obtidoEm: string | null;
        criadoEm: string;
        contatos: number;
        declarante: string | null;
      }>;
    }>("/api/lotes"),
  criarLote: (dados: unknown) =>
    post("/api/lotes", dados) as Promise<{
      id: string;
      rotulo: string;
      contatos: Array<{ telefone: string; nome: string | null }>;
      lidas: number;
      descartadas: number;
      avisos: string[];
      motivo: string;
    }>,
  contatosDoLote: (id: string) =>
    chamar<{ rotulo: string; contatos: Array<{ telefone: string; nome: string | null }> }>(
      `/api/lotes/${id}/contatos`,
    ),

  /* Leads */
  leads: (f?: Record<string, string | number | undefined>) =>
    chamar<RespostaLeads>(`/api/leads${query(f)}`),
  atribuirLead: (id: string, responsavelId: string) =>
    post(`/api/leads/${id}/responsavel`, { responsavelId }),

  /* CRM — o funil do comercial.
     Um GET só alimenta as quatro visões: Kanban, Lista, Funil e Foco são
     arranjos do mesmo dado, e trocar de visão não vai ao servidor. */
  crm: (f?: Record<string, string | number | undefined>) =>
    chamar<RespostaCrm>(`/api/crm${query(f)}`),
  crmFicha: (id: string) => chamar<FichaCard>(`/api/crm/cards/${id}`),
  crmConversa: (id: string) => chamar<ConversaDoCard>(`/api/crm/cards/${id}/conversa`),
  crmCriarCard: (dados: unknown) =>
    post("/api/crm/cards", dados) as Promise<{ id: string; contatoId: string; motivo: string }>,
  crmMover: (id: string, dados: unknown) =>
    chamar<{ id: string; motivo: string }>(`/api/crm/cards/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),
  crmObservar: (id: string, texto: string) =>
    post(`/api/crm/cards/${id}/observacoes`, { texto }) as Promise<{ motivo: string }>,
  crmEditarCliente: (id: string, dados: unknown) =>
    chamar<{ id: string; motivo: string }>(`/api/crm/cards/${id}/cliente`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),
  crmResponder: (id: string, texto: string) =>
    post(`/api/crm/cards/${id}/mensagens`, { texto }) as Promise<{
      enviadoPor: string | null;
      motivo: string;
    }>,
  crmImportarLeads: (dados: unknown) =>
    post("/api/crm/importar-leads", dados) as Promise<{ criados: number; motivo: string }>,
  crmImportarLote: (dados: unknown) =>
    post("/api/crm/importar-lote", dados) as Promise<{ criados: number; motivo: string }>,
  crmEtapas: () =>
    chamar<{
      funil: { id: string; nome: string };
      itens: Array<{
        id: string; nome: string; ordem: number; tom: string; tipo: string;
        instrucao: string | null; ativa: boolean; cards: number;
      }>;
    }>("/api/crm/etapas"),
  crmCriarEtapa: (dados: unknown) =>
    post("/api/crm/etapas", dados) as Promise<{ id: string; motivo: string }>,
  crmEditarEtapa: (id: string, dados: unknown) =>
    chamar<{ id: string; motivo: string }>(`/api/crm/etapas/${id}`, {
      method: "PATCH",
      body: JSON.stringify(dados),
    }),

  /* Usuários */
  usuarios: () => chamar<{ itens: UsuarioResumo[] }>("/api/usuarios"),
  // Criar usuário devolve a senha temporária — é a única vez que ela existe
  // em texto. Depois disso só há hash, e a saída é redefinir.
  criarUsuario: (dados: unknown) =>
    post("/api/usuarios", dados) as Promise<{ id: string; senhaTemporaria: string }>,
  redefinirSenha: (id: string) =>
    post(`/api/usuarios/${id}/senha`, {}) as Promise<{ senhaTemporaria: string }>,
  editarUsuario: (id: string, dados: unknown) =>
    chamar(`/api/usuarios/${id}`, { method: "PATCH", body: JSON.stringify(dados) }),
  excluirUsuario: (id: string) =>
    chamar(`/api/usuarios/${id}`, { method: "DELETE" }),

  /* Configurações e motor */
  config: () => chamar<Configuracoes>("/api/config"),
  salvarConfig: (dados: unknown) =>
    chamar("/api/config", { method: "PATCH", body: JSON.stringify(dados) }),
  motor: () =>
    chamar<{ ligado: boolean; ultimaExecucao: string | null }>("/api/motor"),
  ligarMotor: () => post("/api/motor/ligar"),
  pararMotor: () => post("/api/motor/parar"),
};

/**
 * Hook de carga com estado explícito.
 * Erro de sessão sobe para a casca decidir; os demais viram mensagem na tela.
 */
export type Carga<T> =
  | { fase: "carregando" }
  | { fase: "pronto"; dados: T }
  | { fase: "erro"; mensagem: string };
